import {
  getDocs,
  query,
  collection,
  limit,
  where,
  QueryConstraint,
  orderBy,
  OrderByDirection,
} from "firebase/firestore";
import { db } from "../firebase";

import type { CollectionReference } from "firebase/firestore";
import type { Location } from "../types";
import { FIREBASE_LOCATION_COLLECTION } from "../constants";

async function get<D>(
  path: string,
  ...queryConstraints: Array<QueryConstraint>
) {
  const q = query(
    collection(db, path) as CollectionReference<D>,
    ...queryConstraints
  );
  const result = await getDocs(q);
  return result.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export function getLocations(params?: {
  busAttendanceId?: string;
  busAttendanceIds?: Array<string>;
  limit?: number;
  sort?: { [key in keyof Location]?: OrderByDirection };
  timestamp?: { "<="?: number; ">="?: number };
}) {
  const queryConstraints: Array<QueryConstraint> = [];

  if (params?.busAttendanceId) {
    queryConstraints.push(
      where("busAttendanceId", "==", Number(params.busAttendanceId))
    );
  }
  if (params?.busAttendanceIds) {
    queryConstraints.push(
      where("busAttendanceId", "in", params.busAttendanceIds.map(Number))
    );
  }
  if (params?.limit) {
    queryConstraints.push(limit(params.limit));
  }
  if (params?.timestamp) {
    Object.entries(params.timestamp).forEach(([opStr, value]) => {
      queryConstraints.push(
        where("timestamp", opStr as keyof typeof params.timestamp, value)
      );
    });
  }

  if (params?.sort) {
    Object.entries(params.sort).map(([key, direction]) =>
      queryConstraints.push(orderBy(key, direction))
    );
  }

  return get<Location>(
    FIREBASE_LOCATION_COLLECTION || "locations",
    ...queryConstraints
  );
}

export type DebugLogState =
  | "onLocation"
  | "onMotionChange"
  | "onActivityChange"
  | "onProviderChange"
  | "watchPosition"
  | "watchPositionError"
  | "readyState";

export type DebugLog = {
  createdAt: { seconds: number };
} & Record<DebugLogState, string>;

export async function getTrackingDebugLogs() {
  const response = await get<DebugLog>(
    "bgLocationTrackingDebug",
    orderBy("createdAt", "asc")
  );

  return response.map((item) => {
    return Object.keys(item).reduce((acc, itemKey) => {
      if (["createdAt", "id"].includes(itemKey)) {
        return acc;
      }

      acc.id = item.id;
      acc.type = itemKey as DebugLogState;
      acc.createdAt = item.createdAt.seconds * 1000;
      acc.data = JSON.parse(item[itemKey as DebugLogState]);
      return acc;
    }, {} as { id: string; type: DebugLogState; createdAt: number; data: Record<string, any> });
  });
}
